<template>
  <div class="modal fade" :id="id" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <form @submit.prevent="submitForm" class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{edit ? $t('projects.reports.update') : $t('projects.reports.create')}}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
                  <span class="svg-icon svg-icon-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" 
                            fill="currentColor" />
                      <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                            fill="currentColor" />
                    </svg>
                  </span>
            </div>
          </div>
          <div class="modal-body">  
            <RequiredText />
            <div class="row mb-5">
              <Input
                container-class="col-12 col-md-6 col-lg-6 form-group"
                :name="`report_title_`+id"
                type="text"
                :label="$t('projects.reports.title')"
                labelClass="form-label fs-6 text-black"
                :inputClass="'form-control form-control-lg form-control-solid'"
                size="lg"
                :isRequired="true" 
                v-model="formData.title"
              />
              <InputDate
                container-class="col-12 col-md-6 col-lg-6 form-group"
                :name="`report_start_date_`+id"
                :label="$t('projects.reports.start_at')"
                labelClass="form-label fs-6 text-black"
                :inputClass="'form-control form-control-solid'"
                :isRequired="true" 
                dateFormat="d-m-Y H:i"
                size="lg"
                :placeholder="$t('projects.reports.start_at')"
                v-model="formData.start_date"
                :enableTime="true"
              /> 
            </div>

            <div class="row mb-5">
              <Select2
                :parent-id="'#'+id"
                containerClass="col-12 col-md-6 col-lg-6"
                :name="'report_step_'+id"
                :multiple="false"
                :label="$t('projects.reports.step')"
                labelClass="form-label fs-6 text-black"
                selectClass="form-select form-select-solid"
                :isRequired="false"
                :placeholderText="$t('projects.reports.step')"
                v-model="formData.category_step_id"
                @update:modelValue="handleStepSelected"
                :clear="true"
              >
                <option v-for="step in projectSteps" :key="step.id" :value="step.id">{{ step.title }}</option>
              </Select2>
              
              <Select2
                :parent-id="'#'+id"
                containerClass="col-12 col-md-6 col-lg-6"
                :name="'report_task_'+id"
                :multiple="false"
                :label="$t('projects.reports.task')"
                labelClass="form-label fs-6 text-black"
                selectClass="form-select form-select-solid"
                :isRequired="false"
                :placeholderText="$t('projects.reports.task')"
                v-model="formData.task_id"
                :clear="true"
              >
                <option v-for="task in tasks" :key="task.id" :value="task.id">{{ task.title }}</option>
              </Select2>
            </div>

            <CKEditor
              :label="$t('projects.reports.description')"
              labelClass="form-label fs-6 text-black"
              :id="`report_description_`+id"
              :content="formData.description"
              :isRequired="false"
              @text="setDescription"
            />

            <div class="mt-5">
              <DropFileZone
                :id="`${id}_report_upload_file`"
                :files="formData.attachFiles"
                @addFiles="addReportFiles"
                @removeFiles="removeReportFiles"
              />
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel" @click="closeModal"></Button>
            <Button btn-class="btn-sm me-3" type="submit" id="submit_btn" btn-text="button.save" size="7px 30px" :is-loading="isLoadingReport"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, onMounted, computed } from "vue";
  import Input from "@/components/Input.vue";
  import Button from "@/components/Button.vue";
  import RequiredText from "@/components/RequiredText.vue";
  import { useStore } from "vuex";
  import "vue3-colorpicker/style.css";
  import Select2 from "@/components/Select2.vue";
  import DropFileZone from "@/components/DropFileZone.vue";
  import InputDate from "@/components/InputDate.vue";
  import CKEditor from "@/components/CKEditor.vue";
  import {useRoute} from 'vue-router';

  const emits = defineEmits(["load"]);

  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    form: {
      type: Object,
      default: () => ({
        id: "",
        project_id: '', 
        title: '', 
        description: '', 
        category_step_id: '', 
        task_id: '', 
        start_date: '',
        end_date: '',
        files: [],
        attachFiles: [],
        delete_files: []
      }),
    },
    edit: {
      type: Boolean,
      default: false,
    },
    projectSteps: {
      type: Array,
      default: []
    }
  });

  const store = useStore();
  const route = useRoute()
  const isLoadingReport = ref(false);
  const formData = ref({ ...props.form });
  const tasks = ref([])

  const stepTasks = computed(() => store.getters['projects/getProjectStepTasks'])

  watch(
    () => props.form,
    (newForm) => {
      formData.value = { ...newForm };
    },
    { deep: true }
  );

  watch(
    () => props.form.category_step_id,
    (value) => {
      if(value) {
        handleStepSelected(value)
      }
    }
  );

  function addReportFiles(files){
    for(let file of files) {
      formData.value.files.push(file)
      formData.value.attachFiles.push(file)
    }
  }

  function removeReportFiles(index) {
    let fileToDelete = formData.value.attachFiles.splice(index, 1)
    formData.value.delete_files.push(fileToDelete[0]?.id)
  }

  function closeModal() {
    $("#" + props.id).modal("toggle");
    formData.value.id = "";
    formData.value.title = '';
    formData.value.description = '';
    formData.value.category_step_id = '';
    formData.value.task_id = '';
    formData.value.start_date = '';
    formData.value.end_date = '';
    formData.value.files = []
  }

  function submitForm() {
    isLoadingReport.value = true;
    let action = props.edit ? 'projects/updateProjectReport' : 'projects/createProjectReport';
    let message = props.edit ? 'projects.reports.updated_successfully' : 'projects.reports.created_successfully';
    
    store
      .dispatch(action, formData.value)
      .then(() => {
        isLoadingReport.value = false;
        emits("load");
        closeModal();

        window.toastr["success"](i18n.global.t(message));
      })
      .catch((e) => {
        isLoadingReport.value = false;
      });
  }

  function setDescription(description) {
    formData.value.description = description
  }

  function handleStepSelected(stepId) {
    if(stepId) {
      let step = stepTasks.value.find(el => el.id === stepId)
      tasks.value = step?.tasks
    }
  }

  onMounted(() => {
    formData.value.project_id = route.params.id
  })
</script>

<style scoped>
  .color-picker-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }
</style>
